import { Card, CardContent, CardHeader, CardTitle } from "@elit-hair/elit-ui";
import { useTranslation } from "react-i18next";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import type { ReachabilityHourlyPoint } from "../types/reachability-analysis.types";

type ReachabilityHourlyChartProps = {
    data: ReachabilityHourlyPoint[];
};

const CHART_COLORS = {
    reached: "var(--chart-2)",
    not_reached: "var(--chart-4)",
    voicemail: "var(--chart-3)",
    reach_rate: "var(--chart-1)",
} as const;

function ReachabilityTooltip({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: { dataKey: string; value: number; color: string }[];
    label?: string;
}) {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
            <p className="mb-1 font-medium">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} className="text-muted-foreground" style={{ color: entry.color }}>
                    {entry.dataKey}: {entry.dataKey === "reach_rate" ? `${entry.value.toFixed(1)}%` : entry.value}
                </p>
            ))}
        </div>
    );
}

export function ReachabilityHourlyChart({ data }: ReachabilityHourlyChartProps) {
    const { t } = useTranslation();

    return (
        <Card className="border-border/60 shadow-none">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{t("reachability_analysis_hourly_chart_title")}</CardTitle>
            </CardHeader>
            <CardContent className="h-[320px] pb-4">
                {data.length === 0 ? (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                        {t("reachability_analysis_no_data")}
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" />
                            <XAxis dataKey="hourLabel" tick={{ fontSize: 12 }} />
                            <YAxis yAxisId="counts" allowDecimals={false} tick={{ fontSize: 12 }} />
                            <YAxis
                                yAxisId="rate"
                                orientation="right"
                                domain={[0, 100]}
                                tickFormatter={(value) => `${value}%`}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip content={<ReachabilityTooltip />} />
                            <Legend />
                            <Bar
                                yAxisId="counts"
                                dataKey="reached"
                                name={t("reachability_analysis_reached")}
                                stackId="outcomes"
                                fill={CHART_COLORS.reached}
                            />
                            <Bar
                                yAxisId="counts"
                                dataKey="not_reached"
                                name={t("reachability_analysis_not_reached")}
                                stackId="outcomes"
                                fill={CHART_COLORS.not_reached}
                            />
                            <Bar
                                yAxisId="counts"
                                dataKey="voicemail"
                                name={t("answer_status_voicemail")}
                                stackId="outcomes"
                                fill={CHART_COLORS.voicemail}
                            />
                            <Line
                                yAxisId="rate"
                                type="monotone"
                                dataKey="reach_rate"
                                name={t("reachability_analysis_reach_rate")}
                                stroke={CHART_COLORS.reach_rate}
                                strokeWidth={2}
                                dot={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}
